import { BORROW, DEPOSIT } from "@/utils/constants"
import { Box, Stack, Tab, Tabs } from "@mui/material"
import { useEffect, useState } from "react"
import { ViewProps } from "./types"

import { fetchPriceChange } from "@/apis/lendingRequests"
import { priceChangeState } from "@/atoms/priceChangeState"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { useSetAtom } from "jotai"
import BorrowView from "./components/BorrowView"
import SupplyView from "./components/SupplyView"

import FireOrangeIcon from "@/assets/icons/fire_orange.svg"
import Image from "next/image"

const View = (props: ViewProps) => {
  const {
    tab,
    handleTabChange,
    doesHaveBorrow,
    doesHaveSupply,
    supplyToEarnApys,
    supplyToEarnRewards,
    handleOpenSupplyWithdrawPopup,
    selectedBorrowHeader,
    borrowHeaderAprSelectItems,
    handleChangeSelectHeader,
    handleOpenBorrowRepayPopup,
  } = props

  const { address, isSolanaChain } = useWalletConnect()
  const setPriceChange = useSetAtom(priceChangeState)

  const trans = useTrans()
  const [hasMounted, setHasMounted] = useState(false)

  // This is for the tabbing. This component will be rendered on client-side
  // Will be added for skeleton layout in the future.
  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    if (!address) return
    const fetchPriceChangeData = async () => {
      if (fetchPriceChangeData.isFetching) return
      fetchPriceChangeData.isFetching = true
      const data = await fetchPriceChange(address)
      fetchPriceChangeData.isFetching = false
      if (data?.error) return
      if (!!data?.report && Object.keys(data?.report).length > 0) {
        setPriceChange(data?.report)
      }
    }
    fetchPriceChangeData.isFetching = false

    const interval = setInterval(fetchPriceChangeData, 60000) // 1 minute interval
    fetchPriceChangeData() // Initial call
    return () => clearInterval(interval) // Cleanup on unmount
  }, [setPriceChange, address])

  if (!hasMounted) {
    return <></>
  }

  return (
    <Stack gap={3} data-testid='my-supply-borrow-view'>
      <Tabs
        value={tab}
        onChange={handleTabChange}
        TabIndicatorProps={{ sx: { display: "none" } }}
        aria-label='tabs-sypply-borrow'
        variant='fullWidth'
        className='bg-white border border-solid border-secondary rounded-lg p-[2px]'
        id='my-supply-borrow-tabs'
      >
        <Tab
          classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
          className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
          value={DEPOSIT}
          label={trans.dashboard.table.my_supply}
          data-testid='my-supply-tab'
        />
        <Tab
          classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
          className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
          value={BORROW}
          label={
            <Box className='flex justify-center items-center gap-1'>
              {trans.dashboard.table.my_borrow}
              {isSolanaChain ? <Image src={FireOrangeIcon} alt='highlight-icon' width={24} height={24} /> : null}
            </Box>
          }
          data-testid='my-borrow-tab'
        />
      </Tabs>
      {tab === DEPOSIT ? (
        <SupplyView
          doesHaveSupply={doesHaveSupply}
          supplyToEarnApys={supplyToEarnApys}
          supplyToEarnRewards={supplyToEarnRewards}
          handleOpenSupplyWithdrawPopup={handleOpenSupplyWithdrawPopup}
        />
      ) : (
        <BorrowView
          doesHaveBorrow={doesHaveBorrow}
          selectedBorrowHeader={selectedBorrowHeader}
          borrowHeaderAprSelectItems={borrowHeaderAprSelectItems}
          handleChangeSelectHeader={handleChangeSelectHeader}
          handleOpenBorrowRepayPopup={handleOpenBorrowRepayPopup}
        />
      )}
    </Stack>
  )
}

export default View
